import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  ArrowUpDown,
  Copy,
  FileBarChart,
  Pencil,
  Plus,
  Trash2,
  Zap,
} from 'lucide-react'
import Button from '../../components/Button'
import { useRemembered } from '../../components/listMemory'
import ConfirmDialog from '../../components/ConfirmDialog'
import DataTable from '../../components/DataTable'
import FilterBar from '../../components/FilterBar'
import OutcomeShare from '../../components/OutcomeShare'
import PageHeader from '../../components/PageHeader'
import RowActions from '../../components/RowActions'
import StatusBadge from '../../components/StatusBadge'
import { useToast } from '../../components/toastContext'
import ProgramOrderModal from './ProgramOrderModal'
import { cloneProgram, deleteProgram, listPrograms, saveProgramsOrder } from './programsService'
import { strings as S } from './strings'
import './ProgramsPage.css'
import { serverText } from '../../lib/i18n'

/**
 * ادارة البرامج التدريبية — Views/ProgramsManagement/Table.cshtml.
 *
 * The four dials repeat the dashboard aggregate, so both read the same figures.
 * The notify action is disabled on inactive programmes, keeping the legacy's
 * separate tooltip for that state.
 */
export default function ProgramsPage() {
  const navigate = useNavigate()
  const toast = useToast()
  const [query, setQuery] = useRemembered('programs:query', '')
  const [rows, setRows] = useState([])
  const [shares, setShares] = useState(null)
  const [loading, setLoading] = useState(true)
  /* A FAILED load is not an empty one: without this the table renders its "no records"
     state for a database fault, which tells the operator the opposite of the truth. */
  const [loadError, setLoadError] = useState(null)
  const [pendingDelete, setPendingDelete] = useState(null)
  const [reloadKey, setReloadKey] = useState(0)
  const [orderOpen, setOrderOpen] = useState(false)
  const [savingOrder, setSavingOrder] = useState(false)

  /** One place that turns an action's outcome into a message and a refresh. */
  const run = useCallback(async (call, onDone) => {
    const outcome = await call
    if (!outcome.ok) {
      toast.error(outcome.message || S.actionFailed)
      return outcome
    }
    toast.success(serverText(outcome.message) || S.messages.saved)
    onDone?.()
    setReloadKey((key) => key + 1)
    return outcome
  }, [toast])

  useEffect(() => {
    listPrograms()
      .then((response) => {
        setLoadError(null)
        setRows(response.rows)
      /* `DashboardDto` gives counts, not a pass/fail breakdown, and nothing in this slice
         produces one — so the dials stay hidden rather than showing invented figures.
         `OutcomeShare` renders nothing for a null. Registered in COSMETIC_UI.md. */
        setShares(null)
        setLoading(false)
      })
      .catch((error) => {
        setLoadError(error)
        /* Without this the spinner never stops: a failed call left `loading` true and the
           screen sat there loading nothing, with no reason anywhere. */
        setRows([])
        setLoading(false)
        toast.error(error?.message || S.actionFailed)
      })
  }, [reloadKey, toast])

  const columns = useMemo(
    () => [
      {
        key: 'name',
        header: S.columns.name,
        render: (row) => (
          <Link className="programs__link" to={`/programs/${row.id}`}>
            <bdi>{row.name}</bdi>
          </Link>
        ),
      },
      { key: 'groupsCount', header: S.columns.groups },
      { key: 'coursesCount', header: S.columns.courses },
      {
        key: 'startDate',
        header: S.columns.startDate,
        render: (row) => (
          <span className="programs__date" dir="ltr">
            {row.startDate ?? ''}
          </span>
        ),
      },
      {
        key: 'endDate',
        header: S.columns.endDate,
        render: (row) => (
          <span className="programs__date" dir="ltr">
            {row.endDate ?? ''}
          </span>
        ),
      },
      {
        key: 'isActive',
        header: S.columns.status,
        value: (row) => (row.isActive ? S.status.active : S.status.inactive),
        render: (row) => (
          <StatusBadge tone={row.isActive ? 'success' : 'dark'}>
            {row.isActive ? S.status.active : S.status.inactive}
          </StatusBadge>
        ),
      },
      {
        key: 'actions',
        header: <Pencil size={15} strokeWidth={1.75} />,
        align: 'center',
        width: '112px',
        sortable: false,
        searchable: false,
        render: (row) => (
          <RowActions
            actions={[
              {
                id: 'report',
                icon: FileBarChart,
                label: S.row.report,
                tone: 'info',
                /* The reports screen already picks a programme; it now takes that
                   choice from the URL, so arriving from a row lands on that
                   programme's report rather than on an empty picker. */
                onClick: () => navigate(`/programs-reports?program=${row.id}`),
              },
              { id: 'edit', icon: Pencil, label: S.row.edit, onClick: () => navigate(`/programs/${row.id}/edit`) },
              {
                id: 'clone',
                icon: Copy,
                label: S.row.clone,
                tone: 'primary',
                /* The endpoint takes the copy's two names; the server does not invent
                   them, and a clone with the original's name would not save. */
                /* The two names, per language — not the display name, which is only one
                   of them and would leave the other column holding a copy of it. */
                onClick: () => run(cloneProgram({
                  id: row.id,
                  name: `${row.arabicName} (${S.row.cloneSuffix})`,
                  nameEn: `${row.englishName} (${S.row.cloneSuffix})`,
                })),
              },
              /* TWO ACTIONS ARE NOT HERE, and both were worse than missing.
               *
               * `إرسال تنبيه` — the legacy posts a programme id to
               * `ProgramsManagement/AddNotification`; this project's handler takes a
               * `CourseId`, looks the row up in `Courses` and reads its programme from
               * there. There is no way to name a PROGRAMME to it, and sending a
               * programme id in the course field would schedule the notification
               * against whichever course happens to share that number. The course rows
               * on the detail page carry this action, where it is expressible.
               *
               * `نقل النتائج السابقة` — posts to
               * `ProgramsManagement/MigrateCourseResults`, which does not exist in this
               * project. Its confirm dialog answered with a SUCCESS message and a count
               * taken from the row's own group total: a migration that never ran,
               * reported as having moved a number of users it invented.
               *
               * Both in COSMETIC_UI.md with what they would need. */
              {
                id: 'delete',
                icon: Trash2,
                label: S.row.delete,
                danger: true,
                onClick: () => setPendingDelete(row),
              },
            ]}
          />
        ),
      },
    ],
    [run, navigate],
  )

  return (
    <>
      <PageHeader
        title={S.title}
        count={loading ? undefined : rows.length}
        actions={
          <>
            <Button onClick={() => navigate('/programs/new')}>
              <Plus size={16} strokeWidth={2} />
              {S.actions.create}
            </Button>
            {/* Three buttons of equal weight competed for one job; only `إنشاء` is
                the page's primary action. */}
            <Button variant="soft" onClick={() => navigate('/programs/quick')}>
              <Zap size={16} strokeWidth={1.75} />
              {S.actions.quickCreate}
            </Button>
            {/* Had no handler since the page was built, while `SaveProgramsOrder`
                has been there the whole time. */}
            <Button variant="soft" disabled={loading} onClick={() => setOrderOpen(true)}>
              <ArrowUpDown size={16} strokeWidth={1.75} />
              {S.actions.ordering}
            </Button>
          </>
        }
      />

      {/* The same four dials as the dashboard, at a quarter of the height, so the
          list starts above the fold instead of 170px below it. */}
      <OutcomeShare
        shares={shares}
        labels={S.shares}
        notes={{ incomplete: S.shares.incompleteNote, absent: S.shares.absentNote }}
      />

      <FilterBar search={{ value: query, onChange: setQuery, placeholder: S.columns.name }} />

      <DataTable
        stateKey="programs"
        error={loadError}
        columns={columns}
        rows={rows}
        loading={loading}
        query={query}
        controls={false}
        getRowId={(row) => row.id}
        initialSort={{ key: 'name', dir: 'asc' }}
      />

      <ProgramOrderModal
        open={orderOpen}
        programs={rows}
        saving={savingOrder}
        onClose={() => setOrderOpen(false)}
        onSubmit={async (orderedIds) => {
          setSavingOrder(true)
          await run(saveProgramsOrder(orderedIds), () => setOrderOpen(false))
          setSavingOrder(false)
        }}
      />

      <ConfirmDialog
        open={!!pendingDelete}
        message={S.deleteConfirm}
        /* TYPE THE NAME BACK, as the legacy screen does. Deleting a programme destroys its
           courses, its exams and every learner's history against them — far too much to sit
           behind one press of a red button. */
        confirmPhrase={pendingDelete?.name}
        confirmPhraseLabel={S.deleteByName}
        confirmLabel={S.row.delete}
        cancelLabel={S.close}
        onConfirm={() => run(deleteProgram(pendingDelete.id), () => setPendingDelete(null))}
        onCancel={() => setPendingDelete(null)}
      />
    </>
  )
}
